import React from 'react';
import {TsVideoSubscriberController, VideoSubscriberEventType} from './TsVideoSubscriberController';
import {observer} from 'mobx-react';

export interface DetachableConfig {
    enabled: boolean;
    condition: boolean;
    targetSelector: string;
    position?: 'append' | 'prepend';
}

export interface TsVideoSubscriberProps {
    controller: TsVideoSubscriberController;
    className?: string;
    children?: React.ReactNode;
    detachable?: DetachableConfig;
}

@observer
export class TsVideoSubscriber extends React.Component<TsVideoSubscriberProps> {
    private containerRef = React.createRef<HTMLDivElement>();
    private originalParent: HTMLElement | null = null;
    private detachTarget: HTMLElement | null = null;
    private isDetached: boolean = false;

    private isTouchOnFloatingToolbar = (event: React.TouchEvent): boolean => {
        const target = event.target;
        if (target instanceof Element && target.closest('.floating-toolbar')) {
            return true;
        }

        const touch = event.changedTouches?.[0] || event.touches?.[0];
        if (!touch) {
            return false;
        }

        const topElementAtTouch = document.elementFromPoint(touch.clientX, touch.clientY) as Element | null;
        return Boolean(topElementAtTouch?.closest('.floating-toolbar'));
    };

    componentDidMount() {
        const {controller} = this.props;
        const container = this.containerRef.current;

        if (controller && container) {
            controller.setElement(container);
            if (controller.videoState.enabled) {
                if (!controller.videoSubscriber) {
                    controller.init();
                }

                controller.applySettings();
            }
        }

        // Initialize detachable widget if enabled
        if (this.props.detachable?.enabled) {
            this.initDetachable();
            this.updateDetachState(this.props.detachable.condition);
        }
    }

    componentDidUpdate(prevProps: TsVideoSubscriberProps) {
        const prevDetachable = prevProps.detachable;
        const currentDetachable = this.props.detachable;
        const {controller} = this.props;

        // Watch for detach condition changes
        if (currentDetachable?.enabled && prevDetachable?.condition !== currentDetachable?.condition) {
            this.updateDetachState(currentDetachable.condition);
            setTimeout(() => controller.applySettings(), 0);
        }
    }

    componentWillUnmount() {
        // Restore to original position before unmounting
        if (this.isDetached && this.originalParent && this.containerRef.current) {
            this.originalParent.appendChild(this.containerRef.current);
        }

        if (this.props.controller) {
            this.props.controller.cleanupSubscriber();
        }
    }

    private handleClick = (event: React.MouseEvent) => {
        const target = event.target as HTMLElement;
        // Only handle clicks on the video or canvas, not on UI overlays (mute, pause, etc.)
        if (target.tagName !== 'VIDEO' && !target.closest('canvas')) {
            return;
        }
        if (this.props.controller.canTriggerClick()) {
            this.props.controller.sendEvent(VideoSubscriberEventType.on_video_clicked, event);
        }
    };

    private initDetachable() {
        const container = this.containerRef.current;
        if (!container || !this.props.detachable) return;

        // Store original parent
        this.originalParent = container.parentElement;

        // Find detach target
        const targetSelector = this.props.detachable.targetSelector;
        if (targetSelector) {
            const targets = document.querySelectorAll(targetSelector);
            this.detachTarget = targets.length > 0 ? (targets[targets.length - 1] as HTMLElement) : null;
        }
    }

    private updateDetachState(shouldDetach: boolean) {
        if (shouldDetach === this.isDetached) {
            return; // No change needed
        }

        const container = this.containerRef.current;
        if (!container) return;

        // Determine target container
        const targetContainer = shouldDetach ? this.detachTarget : this.originalParent;

        if (!targetContainer) {
            console.warn('TsVideoSubscriber: detach target not found');
            return;
        }

        // Move the element
        const position = this.props.detachable?.position || 'append';
        if (position === 'prepend') {
            targetContainer.prepend(container);
        } else {
            targetContainer.append(container);
        }

        this.isDetached = shouldDetach;
    }

    private handleTouch = (event: React.TouchEvent) => {
        const target = event.target as HTMLElement;

        // Do not intercept toolbar touches. Let toolbar/DraggblePanel handle tap/click natively.
        if (this.isTouchOnFloatingToolbar(event)) {
            return;
        }

        if (target.closest('canvas')) {
            // Canvas (drawing/annotation): restore original behaviour - no preventDefault so
            // native canvas touch handling is not interrupted.
            if (this.props.controller.canTriggerClick()) {
                this.props.controller.sendEvent(VideoSubscriberEventType.on_video_clicked, event);
            }
            return;
        }

        // Always prevent default for non-canvas touches to block ghost clicks that tablets
        // fire ~300ms after touchend and could land on the canvas/video, triggering a snapsho>
        event.preventDefault();

        if (target.tagName !== 'VIDEO') {
            // UI element (mute, pause, etc.) - manually dispatch a click to replace the preve>
            target.click();
            return;
        }

        if (this.props.controller.canTriggerClick()) {
            this.props.controller.sendEvent(VideoSubscriberEventType.on_video_clicked, event);
        }
    };

    render() {
        const {controller, className, children} = this.props;
        const videoState = controller?.videoState;

        return (
            <div
                ref={this.containerRef}
                className={className}
                style={this.isDetached ? {pointerEvents: 'none' as const} : undefined}
                onClick={this.handleClick}
                onTouchEnd={this.handleTouch}>
                {/* Annotation canvas for scanning mode */}
                <div
                    className='annotations-wrapper'
                    style={{
                        display: videoState?.focus && videoState?.mode === videoState?.MODES?.SCANNING ? '' : 'none'
                    }}>
                    <canvas className='video-annotations'></canvas>
                </div>

                {/* Local drawing canvas (dashboard magic marker) - explicit pointerEvents ensures it receives events when parent toggles */}
                {videoState?.enableDashboardMagicMarker && (
                    <canvas
                        className='local-drawing-canvas'
                        ref={(canvas) => canvas && controller?.initLocalDrawingCanvas(canvas)}
                        style={{
                            display: videoState?.focus && !videoState?.isPaused ? '' : 'none',
                            pointerEvents:
                                videoState?.focus && !videoState?.isPaused ? ('auto' as const) : ('none' as const)
                        }}
                    />
                )}

                {/* Remote drawing canvas (customer magic marker) */}
                {videoState?.enableCustomerMagicMarker && <canvas className='remote-drawing-canvas' />}

                {/* Render any children passed from parent */}
                {children}
            </div>
        );
    }
}
